import {
	ClampToEdgeWrapping,
	DataTexture,
	FileLoader,
	HalfFloatType,
	LinearFilter,
	LinearMipMapLinearFilter,
	LinearSRGBColorSpace,
	RGBAFormat,
	UVMapping,
} from "three";

import { UltraHDRLoader } from "three/addons/loaders/UltraHDRLoader.js";

export class DoppleHDRLoader extends UltraHDRLoader {
	parseWithMetaData(
		buffer: ArrayBuffer,
		metaData: { gamma: number },
		onLoad: (value: {
			data: Uint8Array;
			width: number;
			height: number;
			format: typeof RGBAFormat;
			type: typeof HalfFloatType;
		}) => void,
	) {
		const xmpMetadata: {
			version: string | null;
			baseRenditionIsHDR: boolean | null;
			gainMapMin: number | null;
			gainMapMax: number | null;
			gamma: number | null;
			offsetSDR: number | null;
			offsetHDR: number | null;
			hdrCapacityMin: number | null;
			hdrCapacityMax: number | null;
		} = {
			version: null,
			baseRenditionIsHDR: null,
			gainMapMin: null,
			gainMapMax: null,
			gamma: null,
			offsetSDR: null,
			offsetHDR: null,
			hdrCapacityMin: null,
			hdrCapacityMax: null,
		};
		const textDecoder = new TextDecoder();

		const data = new DataView(buffer);

		let byteOffset = 0;
		const sections = [];

		while (byteOffset < data.byteLength) {
			const byte = data.getUint8(byteOffset);

			if (byte === 0xff) {
				const leadingByte = data.getUint8(byteOffset + 1);

				if (
					[
						/* Valid section headers */
						0xd8, // SOI
						0xe0, // APP0
						0xe1, // APP1
						0xe2, // APP2
					].includes(leadingByte)
				) {
					sections.push({
						sectionType: leadingByte,
						section: [byte, leadingByte],
						sectionOffset: byteOffset + 2,
					});

					byteOffset += 2;
				} else {
					sections[sections.length - 1].section.push(byte, leadingByte);

					byteOffset += 2;
				}
			} else {
				sections[sections.length - 1].section.push(byte);

				byteOffset++;
			}
		}

		let primaryImage: Uint8Array | undefined;
		let gainmapImage: Uint8Array | undefined;

		for (const { sectionType, section, sectionOffset } of sections) {
			if (sectionType === 0xe0) {
				/* JPEG Header - no useful information */
			} else if (sectionType === 0xe1) {
				/* XMP Metadata */
				// @ts-ignore
				this._parseXMPMetadata(
					textDecoder.decode(new Uint8Array(section)),
					xmpMetadata,
				);
				xmpMetadata.gamma = metaData.gamma ?? 1;
			} else if (sectionType === 0xe2) {
				/* Data Sections - MPF / EXIF / ICC Profile */

				const sectionData = new DataView(
					new Uint8Array(section.slice(2)).buffer,
				);
				const sectionHeader = sectionData.getUint32(2, false);

				if (sectionHeader === 0x4d504600) {
					const mpfLittleEndian = sectionData.getUint32(6) === 0x49492a00;
					const mpfBytesOffset = 60;

					/* SDR size includes the metadata length, SDR offset is always 0 */

					const primaryImageSize = sectionData.getUint32(
						mpfBytesOffset,
						mpfLittleEndian,
					);
					const primaryImageOffset = sectionData.getUint32(
						mpfBytesOffset + 4,
						mpfLittleEndian,
					);

					/* Gainmap size is an absolute value starting from its offset, gainmap offset needs 6 bytes padding to take into account 0x00 bytes at the end of XMP */
					const gainmapImageSize = sectionData.getUint32(
						mpfBytesOffset + 16,
						mpfLittleEndian,
					);
					const gainmapImageOffset =
						sectionData.getUint32(mpfBytesOffset + 20, mpfLittleEndian) +
						sectionOffset +
						6;

					primaryImage = new Uint8Array(
						data.buffer,
						primaryImageOffset,
						primaryImageSize,
					);

					gainmapImage = new Uint8Array(
						data.buffer,
						gainmapImageOffset,
						gainmapImageSize,
					);
				}
			}
		}

		/* Minimal sufficient validation - https://developer.android.com/media/platform/hdr-image-format#signal_of_the_format */
		if (!xmpMetadata.version) {
			throw new Error("THREE.UltraHDRLoader: Not a valid UltraHDR image");
		}

		if (primaryImage && gainmapImage) {
			//@ts-ignore
			this._applyGainmapToSDR(
				xmpMetadata,
				primaryImage,
				gainmapImage,
				//@ts-ignore
				(hdrBuffer, width, height) => {
					onLoad({
						width,
						height,
						data: hdrBuffer,
						format: RGBAFormat,
						type: this.type as typeof HalfFloatType,
					});
				},
				//@ts-ignore
				(error) => {
					throw new Error(error);
				},
			);
		} else {
			throw new Error("THREE.UltraHDRLoader: Could not parse UltraHDR images");
		}
	}

	async loadWithMetaData(url: string, metaData: { gamma: number }) {
		const texture = new DataTexture(
			this.type === HalfFloatType ? new Uint16Array() : new Float32Array(),
			0,
			0,
			RGBAFormat,
			this.type,
			UVMapping,
			ClampToEdgeWrapping,
			ClampToEdgeWrapping,
			LinearFilter,
			LinearMipMapLinearFilter,
			1,
			LinearSRGBColorSpace,
		);
		texture.generateMipmaps = true;
		texture.flipY = true;

		const loader = new FileLoader(this.manager);
		loader.setResponseType("arraybuffer");
		loader.setRequestHeader(this.requestHeader);
		loader.setPath(this.path);
		loader.setWithCredentials(this.withCredentials);
		const buffer = (await loader.loadAsync(url)) as ArrayBuffer;

		await new Promise((resolve, reject) => {
			try {
				this.parseWithMetaData(buffer, metaData, (texData) => {
					texture.image = {
						data: texData.data,
						width: texData.width,
						height: texData.height,
					};
					texture.needsUpdate = true;
					resolve(undefined);
				});
			} catch (err) {
				reject(err);
			}
		});

		return texture;
	}
}
